// src/pages/website/BlogForm.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Container,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { ArrowBack, Save, Publish, Link, ContentCopy, ExpandMore } from '@mui/icons-material';

// Catégories avec libellés multilingues
const CATEGORY_OPTIONS = [
  { id: 'danse', label: { fr: 'Danse', en: 'Dance', nl: 'Dans' } },
  { id: 'communauté', label: { fr: 'Communauté', en: 'Community', nl: 'Gemeenschap' } },
  { id: 'ateliers', label: { fr: 'Ateliers', en: 'Workshops', nl: 'Workshops' } },
  { id: 'evenements', label: { fr: 'Événements', en: 'Events', nl: 'Evenementen' } }
];

const slugify = (s='') =>
  s.toString()
   .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
   .toLowerCase()
   .replace(/[^a-z0-9]+/g, '-')
   .replace(/(^-|-$)+/g, '');

// Guide de formatage Markdown
const FormattingGuide = () => (
  <Accordion sx={{ mb: 2 }}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography variant="subtitle1">📝 Guide de formatage Markdown</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <List dense>
        <ListItem>
          <ListItemText 
            primary="Titres" 
            secondary="# Titre principal | ## Sous-titre | ### Titre section"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Gras et Italique" 
            secondary="**gras** | *italique* | ~~barré~~"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Listes" 
            secondary="- Liste à puces | 1. Liste numérotée"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Citations" 
            secondary="> Texte de citation"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Liens" 
            secondary="[texte du lien](https://exemple.com)"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Séparateur" 
            secondary="--- (trois tirets)"
          />
        </ListItem>
      </List>
    </AccordionDetails>
  </Accordion>
);

function BlogForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [langTab, setLangTab] = useState('fr'); // FR par défaut
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // État du formulaire avec champs multilingues + fallback (FR à plat)
  const [formData, setFormData] = useState({
    title_i18n: { fr: '', en: '', nl: '' },
    excerpt_i18n: { fr: '', en: '', nl: '' },
    content_i18n: { fr: '', en: '', nl: '' },
    // legacy pour compat
    title: '',
    excerpt: '',
    content: '',
    slug: '',
    coverImage: '',
    author: '',
    socialUrl: '',
    status: 'draft',
    createdAt: null
  });

  // Chargement d'un article existant (avec rétro-compatibilité)
  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const postRef = doc(db, 'blog_posts', id);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists()) {
          const postData = postSnap.data();

          const title_i18n = postData.title_i18n || { fr: postData.title || '', en: '', nl: '' };
          const excerpt_i18n = postData.excerpt_i18n || { fr: postData.excerpt || '', en: '', nl: '' };
          const content_i18n = postData.content_i18n || { fr: postData.content || '', en: '', nl: '' };

          setFormData({
            title_i18n,
            excerpt_i18n,
            content_i18n,
            title: title_i18n.fr || '',
            excerpt: excerpt_i18n.fr || '',
            content: content_i18n.fr || '',
            slug: postData.slug || slugify(title_i18n.fr || ''),
            coverImage: postData.coverImage || '',
            author: postData.author || '',
            socialUrl: postData.socialUrl || '',
            status: postData.status || 'draft',
            createdAt: postData.createdAt || null
          });

          // Catégorie
          if (postData.category && typeof postData.category === 'object') {
            setSelectedCategory(postData.category.id);
          } else if (typeof postData.category === 'string') {
            setSelectedCategory(postData.category);
          }
        }
      } catch (err) {
        setError('Erreur lors du chargement de l\'article');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  const handleLangChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev };
      next[`${field}_i18n`] = { ...prev[`${field}_i18n`], [langTab]: value };
      if (langTab === 'fr') {
        next[field] = value;        // garder FR à plat
        if (field === 'title') next.slug = slugify(value); // maj du slug à partir du titre FR
      }
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Fonctions de copie FR → EN/NL
  const copyFromFRToLang = (lang) => {
    setFormData(prev => ({
      ...prev,
      title_i18n: { ...prev.title_i18n, [lang]: prev.title_i18n.fr || prev.title || '' },
      excerpt_i18n: { ...prev.excerpt_i18n, [lang]: prev.excerpt_i18n.fr || prev.excerpt || '' },
      content_i18n: { ...prev.content_i18n, [lang]: prev.content_i18n.fr || prev.content || '' }
    }));
  };

  const copyFRToAllEmpty = () => {
    setFormData(prev => ({
      ...prev,
      title_i18n: {
        fr: prev.title_i18n.fr || prev.title || '',
        en: prev.title_i18n.en || prev.title_i18n.fr || prev.title || '',
        nl: prev.title_i18n.nl || prev.title_i18n.fr || prev.title || ''
      },
      excerpt_i18n: {
        fr: prev.excerpt_i18n.fr || prev.excerpt || '',
        en: prev.excerpt_i18n.en || prev.excerpt_i18n.fr || prev.excerpt || '',
        nl: prev.excerpt_i18n.nl || prev.excerpt_i18n.fr || prev.excerpt || ''
      },
      content_i18n: {
        fr: prev.content_i18n.fr || prev.content || '',
        en: prev.content_i18n.en || prev.content_i18n.fr || prev.content || '',
        nl: prev.content_i18n.nl || prev.content_i18n.fr || prev.content || ''
      }
    }));
  };

  const handleSubmit = async (e, status = 'draft') => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation minimale
      if (!formData.title_i18n.fr && !formData.title) {
        setError('Le titre français est requis');
        setIsLoading(false);
        return;
      }

      // Préparer les données pour Firestore
      const postData = {
        title_i18n: formData.title_i18n,
        excerpt_i18n: formData.excerpt_i18n,
        content_i18n: formData.content_i18n,
        // legacy pour compatibilité
        title: formData.title_i18n.fr || formData.title || '',
        excerpt: formData.excerpt_i18n.fr || formData.excerpt || '',
        content: formData.content_i18n.fr || formData.content || '',
        slug: formData.slug || slugify(formData.title_i18n.fr || formData.title || ''),
        coverImage: formData.coverImage,
        author: formData.author,
        socialUrl: formData.socialUrl,
        status,
        updatedAt: serverTimestamp(),
        category: selectedCategory ? { 
          id: selectedCategory, 
          label: CATEGORY_OPTIONS.find(cat => cat.id === selectedCategory)?.label || {} 
        } : null
      };

      // Ajouter createdAt si nouvel article
      if (!id) {
        postData.createdAt = serverTimestamp();
      }

      // Sauvegarde
      if (id) {
        await setDoc(doc(db, 'blog_posts', id), postData, { merge: true });
        setSuccess(`Article ${status === 'published' ? 'publié' : 'sauvegardé'} avec succès !`);
      } else {
        await addDoc(collection(db, 'blog_posts'), postData);
        setSuccess(`Article créé et ${status === 'published' ? 'publié' : 'sauvegardé'} avec succès !`);
        navigate('/website/blog');
      }
    } catch (err) {
      setError('Erreur lors de la sauvegarde : ' + err.message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Aperçu du slug
  const slugPreview = useMemo(() => 
    formData.slug || slugify(formData.title_i18n.fr || formData.title || '')
  , [formData.slug, formData.title_i18n.fr, formData.title]);

  if (isLoading && id) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/website/blog')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">
            {id ? 'Modifier l\'article' : 'Nouvel article'}
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={(e) => handleSubmit(e, 'draft')}
            disabled={isLoading}
            startIcon={<Save />}
          >
            Sauvegarder
          </Button>
          <Button
            variant="contained"
            onClick={(e) => handleSubmit(e, 'published')}
            disabled={isLoading}
            startIcon={<Publish />}
          >
            Publier
          </Button>
        </Box>
      </Box>

      {/* Alertes */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box component="form" onSubmit={(e) => handleSubmit(e, 'draft')}>
        {/* Tabs langues */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={langTab} onChange={(e, newValue) => setLangTab(newValue)}>
            <Tab label="Français" value="fr" />
            <Tab label="English" value="en" />
            <Tab label="Nederlands" value="nl" />
          </Tabs>
          
          <Box p={2}>
            {/* Boutons de copie */}
            {langTab !== 'fr' && (
              <Box mb={2}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={() => copyFromFRToLang(langTab)}
                >
                  Copier depuis le français
                </Button>
              </Box>
            )}
            
            {/* Champs par langue */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={`Titre (${langTab.toUpperCase()})`}
                  name="title"
                  value={formData.title_i18n[langTab] || ''}
                  onChange={(e) => handleLangChange('title', e.target.value)}
                  required={langTab === 'fr'}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={`Extrait (${langTab.toUpperCase()})`}
                  name="excerpt"
                  value={formData.excerpt_i18n[langTab] || ''}
                  onChange={(e) => handleLangChange('excerpt', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label={`Contenu (${langTab.toUpperCase()})`}
                  name="content"
                  value={formData.content_i18n[langTab] || ''}
                  onChange={(e) => handleLangChange('content', e.target.value)}
                  helperText="Utilisez le format Markdown pour la mise en forme"
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Guide de formatage */}
        <FormattingGuide />

        {/* Métadonnées communes */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Métadonnées</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slug (URL)"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Link />
                    </InputAdornment>
                  ),
                }}
                helperText={`Aperçu : ${slugPreview}`}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Catégorie"
                >
                  {CATEGORY_OPTIONS.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.label.fr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image de couverture (URL)"
                name="coverImage"
                value={formData.coverImage}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Auteur"
                name="author"
                value={formData.author}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lien social (optionnel)"
                name="socialUrl"
                value={formData.socialUrl}
                onChange={handleChange}
                placeholder="https://instagram.com/..."
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Actions globales */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            variant="outlined"
            onClick={copyFRToAllEmpty}
            disabled={!formData.title_i18n.fr}
          >
            Copier FR vers toutes les langues
          </Button>
          
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={(e) => handleSubmit(e, 'draft')}
              disabled={isLoading}
              startIcon={<Save />}
            >
              Sauvegarder
            </Button>
            <Button
              variant="contained"
              onClick={(e) => handleSubmit(e, 'published')}
              disabled={isLoading}
              startIcon={<Publish />}
            >
              Publier
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default BlogForm;